import {
  DatabaseField,
  DatabaseFieldType,
  ImportedFileType,
  RawDatabaseValue,
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { produce } from 'immer';
import { isEqual } from 'lodash-es';

import { cheerioIterator } from '../../lib/dom';
import { loadHyperlink } from '../../redux/hyperlinks/actions';
import { selectHyperlinkOptional } from '../../redux/hyperlinks/selectors';
import { CommonRootState } from '../../redux/redux';
import {
  encodeHtml,
  generateId,
  getFallbackHyperlinkLabel,
  isValidHyperlinkUrl,
} from '../../utils';
import { FieldType } from '../field-types';

function getCorrectedValue(
  {
    field,
    value,
  }: { field: DatabaseField; value: RawDatabaseValue<DatabaseFieldType.HYPERLINK> },
  state: CommonRootState | undefined
): RawDatabaseValue<DatabaseFieldType.HYPERLINK> {
  if (state && field.settings.resolve) {
    // Update the label if we happen to have the hyperlink in the store
    return produce(value, (updatedValue) => {
      for (const v of updatedValue) {
        if (!v.isLabelExplicit || !v.label) {
          const hyperlink = selectHyperlinkOptional(state, { url: v.url });
          if (hyperlink) {
            v.label = hyperlink.label;
          }
        }
      }
    });
  } else {
    return value;
  }
}

function isValid(value: RawDatabaseValue<DatabaseFieldType.HYPERLINK>[0]): boolean {
  return isValidHyperlinkUrl(value.url);
}

const hyperlinkFieldType: FieldType<DatabaseFieldType.HYPERLINK> = {
  supportedFilterTypes: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.CONTAINS,
    DatabaseFilterType.EMPTY,
  ],

  supportedImportTypes: [ImportedFileType.HTML, ImportedFileType.CSV],

  renderExportView: ({ value }) => {
    return value
      .filter(isValid)
      .map((v) => {
        if (v.isLabelExplicit) {
          return `<a href="${encodeHtml(v.url)}">${encodeHtml(v.label || v.url)}</a>`;
        } else {
          return `<a href="${encodeHtml(
            v.url
          )}" data-card-appearance="inline">${encodeHtml(v.url)}</a>`;
        }
      })
      .join('<br/>');
  },

  parseFromHtml: (el) => {
    return [...cheerioIterator(el.find('a[href]'))].flatMap((a) => {
      const url = a.attr('href') ?? '';
      if (isValidHyperlinkUrl(url)) {
        const text = a.text();
        const isLabelExplicit = a.attr('data-card-appearance') !== 'inline';
        return [
          {
            _id: generateId(),
            url,
            label: text && (isLabelExplicit || text !== url) ? text : undefined,
            isLabelExplicit,
          },
        ];
      }
      return [];
    });
  },

  getInitialValue: () => [],

  isEmptyValue: ({ value }) => value.filter(isValid).length === 0,

  isEqualValue: ({ value: value1 }, { value: value2 }) =>
    isEqual(
      value1.filter(isValid).map((v) => v.url),
      value2.filter(isValid).map((v) => v.url)
    ),

  getStringRepresentation: (context, state) => {
    const value = getCorrectedValue(context, state);
    return value
      .filter(isValid)
      .map((v) => v.label || getFallbackHyperlinkLabel(v.url))
      .join('\n');
  },

  getValidationError: ({ value }) => {
    const invalid = value.find((v) => v.url && !isValidHyperlinkUrl(v.url));

    if (invalid) {
      return 'validation.invalid-url';
    }
  },

  loadDependencies: ({ field, value }, { dispatch }) => {
    if (field.settings.resolve) {
      for (const v of value) {
        dispatch(loadHyperlink(v.url));
      }
    }
  },

  convertFrom: {
    [DatabaseFieldType.TEXT]: ({ value }) => {
      if (isValidHyperlinkUrl(value)) {
        return [{ _id: generateId(), url: value }];
      } else {
        return [];
      }
    },
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: (context, state) => {
      const value = getCorrectedValue(context, state);
      return value
        .filter(isValid)
        .map((v) => v.label ?? v.url)
        .join('\n');
    },
  },
};

export default hyperlinkFieldType;
